import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { getChartSettingsFromSession } from 'home/AssetHierarchy/actions/assetDetails.actions';
import { useTranslation } from 'react-i18next';
import { toggleAnnotationType, setAnnotationCreateMode } from '../actions/annotations.actions';
import { getVisibleTypes, getCreateMode } from '../selectors/annotations.selectors';
import { TYPE_COLOR, getAnnotationIcon } from '../utils/annotationPresentation';
import { ANNOTATION_TYPES, TYPE_LABEL, TYPE_LABEL_I18N_KEY } from '../utils/annotationTypes';
import {
  Container,
  ButtonGroup,
  TypeButton,
  Icon,
  Label,
  CompactInfo,
  ColorDot,
} from './AnnotationControls.styled';

// Order and labels are shared via utils/annotationTypes
// Hide standalone Defects and Condition controls per merge requirement
const BUTTON_ORDER = ANNOTATION_TYPES.filter(t => t !== 'defect_label' && t !== 'machine_condition_change');

// Named export is the unconnected component, so its add-note lifetime can be
// tested directly without a store.
export function AnnotationControls(props) {
  const { t } = useTranslation();
  const {
    visibleTypes,
    compact,
    scopeKey,
    toggleAnnotationType,
    createMode,
    setAnnotationCreateMode
  } = props;

  const handleAddNoteToggle = useCallback(() => {
    setAnnotationCreateMode(!createMode);
  }, [setAnnotationCreateMode, createMode]);

  // Add-note mode is page-scoped UI state, so it must not outlive the context it
  // was armed in. Clearing it on unmount alone was not enough: views that keep
  // these controls mounted while swapping what is charted (selecting another tag
  // in the hierarchy, or switching a tab that leaves the trend view rendered)
  // never unmount the component, so create-mode stayed armed and the next click
  // on a chart dropped a note on a different asset than the user armed.
  // Listing `scopeKey` here re-runs the cleanup whenever the charted entity
  // changes, disarming create-mode on both paths with one effect.
  useEffect(() => () => setAnnotationCreateMode(false), [setAnnotationCreateMode, scopeKey]);

  const handleTypeToggle = useCallback((type) => {
    toggleAnnotationType(type);
    const chartSettings = getChartSettingsFromSession();
    const current = (chartSettings && chartSettings.visibleAnnotationTypes) || visibleTypes || [];
    const next = current.includes(type) ? current.filter(t => t !== type) : [...current, type];
    const updated = { ...chartSettings, visibleAnnotationTypes: next };
    sessionStorage.setItem('chartSettings', JSON.stringify(updated));
  }, [toggleAnnotationType, visibleTypes]);

  if (compact) {
    const activeCount = visibleTypes.length;
    return (
      <CompactInfo>
        {t('annotations.controls.compactActiveFilters', {
          defaultValue: '{{count}} filter active',
          count: activeCount
        })}
      </CompactInfo>
    );
  }

  return (
    <Container>
      <ButtonGroup>
        {BUTTON_ORDER.map((type) => {
          const isActive = visibleTypes.includes(type);
          const color = TYPE_COLOR[type] || '#757575';
          const icon = getAnnotationIcon(type, { size: 14, color }) || '📝';
          const displayName = t(TYPE_LABEL_I18N_KEY[type] || '', { defaultValue: TYPE_LABEL[type] || type });

          return (
            <TypeButton
              key={type}
              isActive={isActive}
              color={color}
              onClick={() => handleTypeToggle(type)}
              title={t('annotations.controls.toggleTitle', {
                defaultValue: '{{action}} {{name}}',
                action: isActive ? t('annotations.controls.actions.hide', { defaultValue: 'Hide' }) : t('annotations.controls.actions.show', { defaultValue: 'Show' }),
                name: displayName
              })}
              aria-pressed={isActive}
              aria-label={t('annotations.controls.toggleTitle', {
                defaultValue: '{{action}} {{name}}',
                action: isActive ? t('annotations.controls.actions.hide', { defaultValue: 'Hide' }) : t('annotations.controls.actions.show', { defaultValue: 'Show' }),
                name: displayName
              })}
            >
              <ColorDot color={color} isActive={isActive} />
              <Icon>{icon}</Icon>
              <Label>{displayName}</Label>
            </TypeButton>
          );
        })}

        <TypeButton
          isActive={createMode}
          color="#2CB34A"
          onClick={handleAddNoteToggle}
          title={t('annotations.controls.addNote', { defaultValue: 'Add note' })}
          aria-pressed={createMode}
          aria-label={t('annotations.controls.addNote', { defaultValue: 'Add note' })}
        >
          <ColorDot color="#2CB34A" isActive={createMode} />
          <Icon>+</Icon>
          <Label>
            {createMode
              ? t('annotations.controls.addNoteActive', { defaultValue: 'Click or drag on chart…' })
              : t('annotations.controls.addNote', { defaultValue: 'Add note' })}
          </Label>
        </TypeButton>
      </ButtonGroup>

      {/* Selected range display removed per request */}
    </Container>
  );
}

AnnotationControls.propTypes = {
  visibleTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  toggleAnnotationType: PropTypes.func.isRequired,
  setAnnotationCreateMode: PropTypes.func.isRequired,
  createMode: PropTypes.bool,
  compact: PropTypes.bool,
  // Identifies the entity currently being charted. Changing it disarms
  // add-note mode; must be a stable primitive (an id), not a fresh object.
  scopeKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

AnnotationControls.defaultProps = {
  createMode: false,
  compact: false,
  scopeKey: null
};

const mapStateToProps = state => ({
  visibleTypes: getVisibleTypes(state),
  createMode: getCreateMode(state)
});

const mapDispatchToProps = {
  toggleAnnotationType,
  setAnnotationCreateMode
};

export default connect(mapStateToProps, mapDispatchToProps)(AnnotationControls);
